// Pure driving model: km/h in, km/h out. No Phaser, no DOM, no config imports.
// `spec` is one entry of the server car catalog:
//   { fuel: 'pb95'|'on'|'ev', gears: number|null, accel_0_50_s, brake_50_0_s, vmax_kmh, consumption }

import { PX_PER_KMH, MAX_VISUAL_PX_S, REVERSE_KMH } from './carCatalog.mjs';

const SHIFT_LOW_KMH = 20;      // first upshift
const SHIFT_TOP_FRACTION = 0.9; // last upshift, as a fraction of vmax
const COAST_DRAG = 0.6 * 3.6;  // km/h per second of engine braking + rolling drag
const COAST_AIR = 0.002;       // km/h per second, per (km/h)^2
const EV_COAST_FRACTION = 0.35; // regen braking, as a fraction of full braking

const clamp = (n, lo, hi) => (n < lo ? lo : n > hi ? hi : n);
const isEv = (spec) => spec.fuel === 'ev';

/** A fresh at-rest driving state. `facing` is -1 (left) or 1 (right). */
export function createDriveState(facing = -1) {
  return { v: 0, gear: 1, rpm: 0, shiftTimer: 0, facing, reversing: false, uturning: false };
}

/** Gearbox pause after an upshift, in seconds. EVs and single-speed cars: none. */
export function dipSeconds(spec) {
  if (!spec || isEv(spec) || !spec.gears || spec.gears < 2) return 0;
  if (spec.gears >= 8) return 0.08;
  if (spec.gears === 7) return 0.2;
  if (spec.gears === 6) return 0.22;
  return 0.25;
}

/** Speeds (km/h) at which the car shifts up: `gears - 1` points, geometrically spread. */
export function shiftPoints(spec) {
  const n = (spec && spec.gears ? spec.gears : 1) - 1;
  if (n <= 0) return [];
  const lo = SHIFT_LOW_KMH;
  const hi = Math.max(lo * 1.2, spec.vmax_kmh * SHIFT_TOP_FRACTION);
  const pts = [];
  for (let i = 0; i < n; i++) pts.push(lo * (hi / lo) ** (n === 1 ? 0 : i / (n - 1)));
  return pts;
}

/** Gear (1-based) and position within its speed band (0..1) at speed `v`. */
function gearAt(v, spec, points) {
  const vmax = spec.vmax_kmh;
  if (points.length === 0) return { gear: 1, band: clamp(v / vmax, 0, 1) };
  let gear = 1;
  for (const p of points) if (v >= p) gear += 1;
  const lo = gear === 1 ? 0 : points[gear - 2];
  const hi = gear - 1 < points.length ? points[gear - 1] : vmax;
  return { gear, band: hi > lo ? clamp((v - lo) / (hi - lo), 0, 1) : 0 };
}

// Torque factor across a gear band; flat-ish, a little softer off the bottom.
const torque = (r) => 0.75 + 0.5 * r - 0.25 * r * r;

/** Visual px/s for a speed in km/h, capped so fast cars stay watchable. */
export function pxPerSec(kmh) {
  return Math.min(kmh * PX_PER_KMH, MAX_VISUAL_PX_S);
}

/** Litres (or kWh) burned over `meters` at `kmh`. Faster driving burns more. */
export function fuelBurn(meters, kmh, spec) {
  if (!(meters > 0)) return 0;
  const load = 0.7 + 0.6 * (kmh / 100) ** 2;
  return (meters / 100000) * spec.consumption * load;
}

/** kWh recovered while slowing over `metersDecel` — 10 % of the burn, EVs only. */
export function regen(metersDecel, spec, kmh = 50) {
  if (!isEv(spec)) return 0;
  return 0.1 * fuelBurn(metersDecel, kmh, spec);
}

// --- acceleration calibration -------------------------------------------------
// `a0` is solved numerically per spec so that simulating the real `step` loop
// reproduces the catalog's 0–50 km/h time (torque curve and shift dips included).

const a0Cache = new Map();

const specKey = (spec) => `${spec.fuel}|${spec.gears}|${spec.accel_0_50_s}|${spec.vmax_kmh}|${spec.brake_50_0_s}`;

function timeTo50(a0, spec) {
  let s = createDriveState();
  const dt = 1 / 60;
  let t = 0;
  while (s.v < 50 && t < 30) {
    s = advance(s, 'throttle', dt, spec, a0);
    t += dt;
  }
  return t;
}

function calibrate(spec) {
  const key = specKey(spec);
  const hit = a0Cache.get(key);
  if (hit !== undefined) return hit;
  // timeTo50 decreases monotonically with a0.
  let lo = 0.1;
  let hi = 5000;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (timeTo50(mid, spec) > spec.accel_0_50_s) lo = mid;
    else hi = mid;
  }
  const a0 = (lo + hi) / 2;
  a0Cache.set(key, a0);
  return a0;
}

// --- the step -----------------------------------------------------------------

function advance(state, input, dt, spec, a0) {
  const s = { ...state };

  if (s.reversing) {
    s.v = input === 'throttle' ? REVERSE_KMH : 0;
    s.gear = 1;
    s.rpm = 0;
    s.shiftTimer = 0;
    return s;
  }

  s.shiftTimer = Math.max(0, (state.shiftTimer || 0) - dt);

  const vmax = spec.vmax_kmh;
  const points = shiftPoints(spec);
  const before = gearAt(state.v, spec, points);
  const headroom = Math.max(0, 1 - state.v / vmax);

  let dv;
  if (input === 'throttle') {
    if (s.shiftTimer > 0) dv = 0; // clutch is out: no drive during the dip
    else if (isEv(spec)) dv = a0 * headroom ** 0.6;
    else dv = a0 * torque(before.band) * headroom;
  } else if (input === 'brake') {
    dv = -(50 / spec.brake_50_0_s);
  } else if (isEv(spec)) {
    dv = -EV_COAST_FRACTION * (50 / spec.brake_50_0_s);
  } else {
    dv = -COAST_DRAG - COAST_AIR * state.v * state.v;
  }

  s.v = clamp(state.v + dv * dt, 0, vmax);

  const after = gearAt(s.v, spec, points);
  if (after.gear > (state.gear || 1)) s.shiftTimer = dipSeconds(spec);
  s.gear = after.gear;
  s.rpm = s.shiftTimer > 0 ? 0.35 : after.band;
  return s;
}

/**
 * One physics tick. Pure: returns a new state, never mutates `state`.
 * `input` is 'throttle' | 'brake' | 'none'; `dt` in seconds.
 * `uturning` is a flag the controller owns — `step` carries it through untouched.
 */
export function step(state, input, dt, spec) {
  return advance(state, input, dt, spec, calibrate(spec));
}
